import { Checkbox, notification, Switch, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { GO_TO_SYNC_DASHBOARD } from '../api/constants';
import NavBar from '../components/common/NavBar';
import { UHIDSetup } from '../components/common/PatientInfoForm';
import {
    CONTACT,
    DEFAULT_PT_COLUMNS,
    FOLLOW_UP,
    keyToLabel,
    LAST_VISITED,
    PT_DETAILS,
} from '../constants';
import {
    amIDoctorOnly,
    getAllPatients,
    getDoc,
    getDoctorSelectorList,
    getMe,
    getMySingleDoctorId,
} from '../store/idbHandler';
import { Patient } from '../store/types/patient';
import { getAgeString } from '../store/utils';
import { track } from '../tracking';
import pushMessageConfig from '../api/pushMessageConfig';
import fetchMessageConfig from '../api/fetchMessageConfig';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
    ProfileRouterParams,
    PROFILE_TABS_TYPE,
    GO_TO_SETTINGS,
    GO_TO_ADD_EKA_CREDITS,
    GO_TO_ABHA_DASHBOARD,
} from '../routes/paths';
import ActivatePayments from './profile/ActivatePayments';
import StaffRescription from './profile/StaffRestriction';
import { useDispatch, useSelector } from 'react-redux';
import {
    getGenieSetting,
    getIsTreatmentPlanEnabled,
    getKhataCredits,
    getRefreshPage,
} from '../store/selectors/session';
import { GlobalState } from '../store/reducers';
import { MessageConfigType, SREV2 } from '../api/pushGenie';
import { toggleShowEkaCommDialog, upsertGenieSetting } from '../store/actions/session';
import NewHeaderComponent from '../components/common/NewHeaderComponent';
import { AntTooltip } from '../components/common/AntTooltip';
import {
    ekaCreditsIcon,
    ekaCreditsIconRed,
    infoIconFilled,
    loaderIcon,
    rightArrowIcon,
    rightArrowIcon2,
    sparklesIcon,
} from '../components/common/Icons';
import { RupeeIcon } from '../components/common/constants/khataUtils';
import AppLoader from '../components/common/AppLoader';
import { getAddPDForms } from './utils';
import ManageTreatmentsContainer from './ManageTreatmentsContainer';
import ManageServicesContainer from '../components/manageServices/ManageServicesContainer';
import AppointmentConfigsContainer from '../components/appointment-configs/AppointmentConfigsContainer';
import ClinicDetailsContainer from '../components/abha/ClinicDetailsContainer';
import ManageTreatmentPackages from '../components/ManageTreatmentPackages/ManageTreatmentPackages';
import { MyQr } from './profile/MyQr';
import DoctorFilterStripUi from '../components/multiDocComponents/DoctorStrip';
import { getAclViewClassName } from '../restrictions/utils';

const infoStripConstant = (
    currBalance: number | undefined,
): {
    bg: string;
    text: string;
    textColor: string;
    fontWeight?: string;
    icon: JSX.Element;
} => {
    if (currBalance && currBalance > 20) {
        return {
            bg: 'linear-gradient(98deg, #215FFF -9.13%, #143999 127.71%)',
            text:
                'Confused about the best way to communicate? Click here to understand the benefits of each method',
            icon: sparklesIcon(),
            textColor: 'text-white',
        };
    } else if (currBalance && currBalance <= 20 && currBalance > 0) {
        return {
            bg: 'linear-gradient(100deg, #95E9C1 2.51%, #D8FF71 134.8%)',
            text: 'Save credits by moving communications to SMS channel',
            icon: <RupeeIcon className="w-10 h-12 fill-darwin-neutral_new-1000" />,
            textColor: 'text-darwin-neutral_new-1000',
        };
    } else {
        return {
            bg: '#EDAE0C',
            text:
                "You've run out of Eka Credits! All your messages will now be sent via Eka Patient App, which is free. ",
            fontWeight: 'font-700',
            icon: infoIconFilled(),
            textColor: 'text-darwin-neutral_new-1000',
        };
    }
};

const PREMIUM_MENUITEMS: {
    key: PROFILE_TABS_TYPE;
    svg: JSX.Element;
    label: string;
    hideArrow?: boolean;
    showOption: boolean;
}[] = [];

const PREMIUM_KEYS = PREMIUM_MENUITEMS.filter((p) => p.showOption === true).map((p) => p.key);

export const MY_PROFILE_CONTAINER = 'my-profile-container';

const HeaderWrapper: React.FC<{ title: string }> = (props) => {
    return (
        <div className="flex flex-col h-full w-full col-span-11 overflow-hidden">
            <NewHeaderComponent title={props.title} />
            {props.children}
        </div>
    );
};

const MySettings: React.FC = () => {
    const { subRoute } = useParams<ProfileRouterParams>();
    const doc = getMe()?.doctor;
    const refreshPage = useSelector(getRefreshPage);

    const history = useHistory();
    const location = useLocation<{ showMessageCommPopover: boolean }>();

    const setActiveMenuItem = (key: PROFILE_TABS_TYPE): void => {
        track('GO_TO_SETTINGS_ITEM', { key });
        history.push(GO_TO_SETTINGS(key));
    };

    useEffect(() => {
        if (subRoute === 'sync') {
            onSync();
        }
        if (!subRoute) {
            setActiveMenuItem('profile');
        }
    }, [subRoute]);

    const onSync = (): void => {
        try {
            const width = 550,
                height = 550,
                left = (screen.width - width) / 2,
                top = (screen.height - height) / 2;
            window.open(
                GO_TO_SYNC_DASHBOARD,
                'Sync',
                'width=' + width + ',height=' + height + ',top=' + top + ',left=' + left + '',
            );
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <div className="flex flex-col w-screen h-full font-lato">
            <Helmet>
                <meta charSet="utf-8" />
                <title>EKA - Dr. {doc?.profile.personal.name.f || ''}</title>
            </Helmet>
            <div
                id={MY_PROFILE_CONTAINER}
                className="grid grid-cols-settingsPage h-full min-w-840 overflow-x-auto"
            >
                <NavBar />

                <div className="flex flex-col col-span-11 overflow-hidden">
                    <NewHeaderComponent title="Settings and preferences" />
                    <div className="flex h-full overflow-hidden">
                        <LeftPanel
                            key={refreshPage?.toString()}
                            setActiveMenuItem={setActiveMenuItem}
                            activeMenuItem={subRoute as PROFILE_TABS_TYPE}
                        />
                        <div className="flex w-full col-span-11 overflow-hidden bg-white">
                            {subRoute === 'invite' && (
                                <iframe className="w-full h-full" src="/invite-frame.html" />
                            )}
                            {subRoute === 'messageConfiguration' && (
                                <MessageConfiguration
                                    showEkaCommPop={location?.state?.showMessageCommPopover}
                                />
                            )}
                            {subRoute === 'manageTreatmentPackages' && (
                                <ManageTreatmentsContainer />
                            )}
                            {subRoute === 'staffRestriction' && <StaffRescription />}
                            {subRoute === 'qr' && <MyQr />}
                            {subRoute === 'activate-payments' && <ActivatePayments />}
                            {subRoute === 'uhidConfig' && (
                                <UHIDSetup
                                    mode="page"
                                    onSuccess={() => {
                                        notification.success({
                                            message: `Successfully updated UHID configuration`,
                                            duration: 5,
                                        });
                                        setActiveMenuItem('invite');
                                    }}
                                />
                            )}
                            {subRoute === 'manage-services' && <ManageServicesContainer />}
                            {subRoute === 'manage-treatment-packages' && (
                                <ManageTreatmentPackages />
                            )}
                            {subRoute === 'appointment-configs' && <AppointmentConfigsContainer />}
                            {subRoute === 'abha-qr' && <ABHAQrContainerComponent />}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ABHAQrContainerComponent: React.FC = () => {
    const isAbdmEnabled = useSelector((state: GlobalState) => getGenieSetting(state, 'abdm'));
    const history = useHistory();

    const gotoAbhaDashboard = () => {
        track('GO_TO_ABHA_DASHBOARD_FROM_SETTINGS');
        history.push(GO_TO_ABHA_DASHBOARD);
    };

    return (
        <div className="flex w-full gap-16 flex-col p-24">
            <p className="text-16 font-700 text-neutral-black pb-0">ABDM Scan & share</p>
            {isAbdmEnabled ? (
                <div className="w-50p">
                    <ClinicDetailsContainer />
                </div>
            ) : (
                <div className="flex flex-col gap-10">
                    <p>Currently you are not enrolled to ABDM</p>
                    <p
                        onClick={gotoAbhaDashboard}
                        className="flex font-700 items-center gap-4 text-12 text-darwin-accent-bright-blue-800 cursor-pointer"
                    >
                        Enroll now{' '}
                        {rightArrowIcon2('fill-darwin-accent-bright-blue-800 font-700 w-12 h-14')}
                    </p>
                </div>
            )}
        </div>
    );
};

const SettingComponent: React.FC<{
    i: any;
    activeMenuItem: PROFILE_TABS_TYPE;
    setActiveMenuItem: (key: PROFILE_TABS_TYPE) => void;
    className: string | undefined;
    borderTop: boolean;
}> = (props) => {
    const { i, activeMenuItem, setActiveMenuItem, borderTop } = props;

    return (
        <div
            key={i.label}
            onClick={() => setActiveMenuItem(i.key)}
            className={`flex cursor-pointer ${
                borderTop && 'border-t border-darwin-neutral_new-200'
            } bg-white p-8 pl-10 justify-between gap-8 ${
                i.key === activeMenuItem
                    ? 'border rounded-8 text-darwin-accent-bright-blue-800 fill-darwin-accent-bright-blue-800 border-darwin-accent-bright-blue-800'
                    : ''
            } ${props.className ?? ''}`}
            style={
                i.key.includes('premium')
                    ? {
                          background: 'linear-gradient(136deg, #FFF8E8 0%, #F8E3AF 100%)',
                          border: 0,
                      }
                    : undefined
            }
        >
            <div className="flex gap-8 items-center">
                <div className={i.key !== activeMenuItem ? 'fill-darwin-neutral_new-800' : ''}>
                    {i.svg}
                </div>
                <p className={i.key === activeMenuItem ? 'font-700' : ''}>{i.label}</p>
            </div>
            {!i.hideArrow && (
                <div>
                    <svg
                        width="25"
                        height="25"
                        viewBox="0 0 28 28"
                        className={`${
                            i.key === activeMenuItem
                                ? 'fill-darwin-accent-bright-blue-800'
                                : 'fill-neutral-5'
                        } cursor-pointer`}
                        preserveAspectRatio="xMidYMid meet"
                    >
                        <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"></path>
                    </svg>
                </div>
            )}
        </div>
    );
};

const Group1Options: React.FC<{
    activeMenuItem: PROFILE_TABS_TYPE;
    setActiveMenuItem: (key: PROFILE_TABS_TYPE) => void;
}> = ({ activeMenuItem, setActiveMenuItem }) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS is incapable to understand the .filter(Boolean)
    const MENU_GROUP_1: {
        key: PROFILE_TABS_TYPE;
        svg: JSX.Element;
        label: string;
        hideArrow?: boolean;
    }[] = [
        amIDoctorOnly() && {
            key: 'invite',
            svg: (
                <svg width="16" height="16" viewBox="0 0 18 15" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.875 4.25C9.875 6.19141 8.28906 7.75 6.375 7.75C4.43359 7.75 2.875 6.19141 2.875 4.25C2.875 2.33594 4.43359 0.75 6.375 0.75C8.28906 0.75 9.875 2.33594 9.875 4.25ZM0.25 13.957C0.25 11.25 2.41016 9.0625 5.11719 9.0625H7.60547C10.3125 9.0625 12.5 11.25 12.5 13.957C12.5 14.3945 12.1172 14.75 11.6797 14.75H1.04297C0.605469 14.75 0.25 14.3945 0.25 13.957ZM14.0312 9.28125V7.53125H12.2812C11.8984 7.53125 11.625 7.25781 11.625 6.875C11.625 6.51953 11.8984 6.21875 12.2812 6.21875H14.0312V4.46875C14.0312 4.11328 14.3047 3.8125 14.6875 3.8125C15.043 3.8125 15.3438 4.11328 15.3438 4.46875V6.21875H17.0938C17.4492 6.21875 17.75 6.51953 17.75 6.875C17.75 7.25781 17.4492 7.53125 17.0938 7.53125H15.3438V9.28125C15.3438 9.66406 15.043 9.9375 14.6875 9.9375C14.3047 9.9375 14.0312 9.66406 14.0312 9.28125Z" />
                </svg>
            ),
            label: 'Invite Patients',
        },
        {
            key: 'qr',
            svg: (
                <svg width="16" height="16" viewBox="0 0 14 13" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.1875 0.625H4.8125C5.52344 0.625 6.125 1.22656 6.125 1.9375V4.5625C6.125 5.30078 5.52344 5.875 4.8125 5.875H2.1875C1.44922 5.875 0.875 5.30078 0.875 4.5625V1.9375C0.875 1.22656 1.44922 0.625 2.1875 0.625ZM4.375 2.375H2.625V4.125H4.375V2.375ZM2.1875 7.625H4.8125C5.52344 7.625 6.125 8.22656 6.125 8.9375V11.5625C6.125 12.3008 5.52344 12.875 4.8125 12.875H2.1875C1.44922 12.875 0.875 12.3008 0.875 11.5625V8.9375C0.875 8.22656 1.44922 7.625 2.1875 7.625ZM4.375 9.375H2.625V11.125H4.375V9.375ZM7.875 1.9375C7.875 1.22656 8.44922 0.625 9.1875 0.625H11.8125C12.5234 0.625 13.125 1.22656 13.125 1.9375V4.5625C13.125 5.30078 12.5234 5.875 11.8125 5.875H9.1875C8.44922 5.875 7.875 5.30078 7.875 4.5625V1.9375ZM9.625 2.375V4.125H11.375V2.375H9.625ZM10.5 12H11.375V12.875H10.5V12ZM13.125 12V12.875H12.25V12H13.125ZM12.25 7.625H13.125V8.5V9.375V11.125H10.5V10.25H9.625V12.875H7.875V10.25V7.625H10.5V8.5H12.25V7.625Z" />
                </svg>
            ),
            label: 'QR for Check-in at clinic',
        },
        amIDoctorOnly() && {
            key: 'abha-qr',
            svg: (
                <svg width="16" height="16" viewBox="0 0 14 13" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.1875 0.625H4.8125C5.52344 0.625 6.125 1.22656 6.125 1.9375V4.5625C6.125 5.30078 5.52344 5.875 4.8125 5.875H2.1875C1.44922 5.875 0.875 5.30078 0.875 4.5625V1.9375C0.875 1.22656 1.44922 0.625 2.1875 0.625ZM4.375 2.375H2.625V4.125H4.375V2.375ZM2.1875 7.625H4.8125C5.52344 7.625 6.125 8.22656 6.125 8.9375V11.5625C6.125 12.3008 5.52344 12.875 4.8125 12.875H2.1875C1.44922 12.875 0.875 12.3008 0.875 11.5625V8.9375C0.875 8.22656 1.44922 7.625 2.1875 7.625ZM4.375 9.375H2.625V11.125H4.375V9.375ZM7.875 1.9375C7.875 1.22656 8.44922 0.625 9.1875 0.625H11.8125C12.5234 0.625 13.125 1.22656 13.125 1.9375V4.5625C13.125 5.30078 12.5234 5.875 11.8125 5.875H9.1875C8.44922 5.875 7.875 5.30078 7.875 4.5625V1.9375ZM9.625 2.375V4.125H11.375V2.375H9.625ZM10.5 12H11.375V12.875H10.5V12ZM13.125 12V12.875H12.25V12H13.125ZM12.25 7.625H13.125V8.5V9.375V11.125H10.5V10.25H9.625V12.875H7.875V10.25V7.625H10.5V8.5H12.25V7.625Z" />
                </svg>
            ),
            label: 'ABDM Scan & share',
        },
    ].filter(Boolean);

    const getGroup1Items = () => [
        ...MENU_GROUP_1.slice(0, 1),
        ...PREMIUM_MENUITEMS.filter((o) => o.showOption === true),
        ...MENU_GROUP_1.slice(1),
    ];

    return (
        <div className="flex flex-col bg-white rounded-8">
            {getGroup1Items().map((i, idx) => (
                <SettingComponent
                    i={i}
                    activeMenuItem={activeMenuItem}
                    setActiveMenuItem={setActiveMenuItem}
                    className={
                        idx === 0
                            ? 'rounded-t-8'
                            : idx === getGroup1Items().length - 1
                            ? 'rounded-b-8'
                            : undefined
                    }
                    borderTop={
                        idx !== 0 &&
                        idx !== getGroup1Items().findIndex((p) => p.key === activeMenuItem) + 1
                    }
                />
            ))}
        </div>
    );
};

const Group2Options: React.FC<{
    activeMenuItem: PROFILE_TABS_TYPE;
    setActiveMenuItem: (key: PROFILE_TABS_TYPE) => void;
}> = ({ activeMenuItem, setActiveMenuItem }) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS is incapable to understand the .filter(Boolean)
    const MENU_GROUP_2: {
        key: PROFILE_TABS_TYPE;
        svg: JSX.Element;
        label: string;
        hideArrow?: boolean;
    }[] = [
        amIDoctorOnly() && {
            key: 'sync',
            svg: (
                <svg width="16" height="16" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.87109 5.30078C2.70703 5.76562 2.1875 6.01172 1.75 5.84766C1.28516 5.68359 1.03906 5.19141 1.20312 4.72656C1.50391 3.87891 1.99609 3.08594 2.65234 2.42969C5.05859 0.0507812 8.91406 0.0507812 11.3203 2.42969L11.7852 2.92188V1.5C11.7852 1.03516 12.1953 0.625 12.6602 0.625C13.1523 0.625 13.5352 1.03516 13.5352 1.5V5C13.5352 5.49219 13.1523 5.875 12.6602 5.875H9.1875C8.69531 5.875 8.3125 5.49219 8.3125 5C8.3125 4.53516 8.69531 4.125 9.1875 4.125H10.5547L10.0898 3.66016C8.36719 1.96484 5.60547 1.96484 3.88281 3.66016C3.41797 4.15234 3.0625 4.69922 2.87109 5.30078ZM1.06641 7.67969C1.09375 7.65234 1.14844 7.65234 1.17578 7.65234C1.23047 7.65234 1.25781 7.625 1.3125 7.625H4.8125C5.27734 7.625 5.6875 8.03516 5.6875 8.5C5.6875 8.99219 5.27734 9.375 4.8125 9.375H3.41797L3.88281 9.86719C5.60547 11.5625 8.36719 11.5625 10.0898 9.86719C10.5547 9.375 10.9102 8.82812 11.1016 8.22656C11.2656 7.76172 11.7852 7.51562 12.2227 7.67969C12.6875 7.84375 12.9336 8.33594 12.7695 8.80078C12.4688 9.64844 11.9766 10.4141 11.3203 11.0977C8.91406 13.4766 5.05859 13.4766 2.65234 11.0977L2.1875 10.6055V12C2.1875 12.4922 1.77734 12.875 1.3125 12.875C0.820312 12.875 0.4375 12.4922 0.4375 12V8.52734C0.4375 8.47266 0.4375 8.41797 0.4375 8.39062C0.4375 8.33594 0.4375 8.30859 0.464844 8.28125C0.492188 8.14453 0.574219 8.00781 0.683594 7.89844C0.792969 7.78906 0.929688 7.70703 1.06641 7.67969Z" />
                </svg>
            ),
            label: 'Sync all appointments',
            hideArrow: true,
        },
    ].filter(Boolean);

    return (
        <div className="flex flex-col bg-white rounded-8">
            {MENU_GROUP_2.map((i, idx) => (
                <SettingComponent
                    i={i}
                    activeMenuItem={activeMenuItem}
                    setActiveMenuItem={setActiveMenuItem}
                    className={
                        MENU_GROUP_2.length === 1
                            ? 'rounded-8'
                            : idx === 0
                            ? 'rounded-t-8'
                            : idx === MENU_GROUP_2.length - 1
                            ? 'rounded-b-8'
                            : undefined
                    }
                    borderTop={
                        idx !== 0 &&
                        idx !== MENU_GROUP_2.findIndex((p) => p.key === activeMenuItem) + 1
                    }
                />
            ))}
        </div>
    );
};

const Group3Options: React.FC<{
    activeMenuItem: PROFILE_TABS_TYPE;
    setActiveMenuItem: (key: PROFILE_TABS_TYPE) => void;
}> = ({ activeMenuItem, setActiveMenuItem }) => {
    const showTreatmentPackages = useSelector(getIsTreatmentPlanEnabled);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS is incapable to understand the .filter(Boolean)
    const MENU_GROUP_3: {
        key: PROFILE_TABS_TYPE;
        svg: JSX.Element;
        label: string;
        hideArrow?: boolean;
    }[] = [
        {
            key: 'uhidConfig',
            svg: (
                <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                    <mask
                        id="mask0"
                        mask-type="alpha"
                        maskUnits="userSpaceOnUse"
                        x="1"
                        y="1"
                        width="16"
                        height="16"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M13.0014 7.99998C13.0014 8.22665 12.9814 8.43998 12.9547 8.65331L14.3614 9.75331C14.488 9.85331 14.5214 10.0333 14.4414 10.18L13.108 12.4866C13.028 12.6333 12.8547 12.6933 12.7014 12.6333L11.0414 11.9666C10.6947 12.2266 10.3214 12.4533 9.91469 12.62L9.66136 14.3866C9.64136 14.5466 9.50136 14.6666 9.33469 14.6666H6.66802C6.50136 14.6666 6.36136 14.5466 6.34136 14.3866L6.08802 12.62C5.68136 12.4533 5.30802 12.2333 4.96136 11.9666L3.30136 12.6333C3.15469 12.6866 2.97469 12.6333 2.89469 12.4866L1.56136 10.18C1.48136 10.0333 1.51469 9.85331 1.64136 9.75331L3.04802 8.65331C3.02136 8.43998 3.00136 8.21998 3.00136 7.99998C3.00136 7.77998 3.02136 7.55998 3.04802 7.34665L1.64136 6.24665C1.51469 6.14665 1.47469 5.96665 1.56136 5.81998L2.89469 3.51331C2.97469 3.36665 3.14802 3.30665 3.30136 3.36665L4.96136 4.03331C5.30802 3.77331 5.68136 3.54665 6.08802 3.37998L6.34136 1.61331C6.36136 1.45331 6.50136 1.33331 6.66802 1.33331H9.33469C9.50136 1.33331 9.64136 1.45331 9.66136 1.61331L9.91469 3.37998C10.3214 3.54665 10.6947 3.76665 11.0414 4.03331L12.7014 3.36665C12.848 3.31331 13.028 3.36665 13.108 3.51331L14.4414 5.81998C14.5214 5.96665 14.488 6.14665 14.3614 6.24665L12.9547 7.34665C12.9814 7.55998 13.0014 7.77331 13.0014 7.99998ZM5.66802 7.99998C5.66802 9.28665 6.71469 10.3333 8.00136 10.3333C9.28802 10.3333 10.3347 9.28665 10.3347 7.99998C10.3347 6.71331 9.28802 5.66665 8.00136 5.66665C6.71469 5.66665 5.66802 6.71331 5.66802 7.99998Z"
                        ></path>
                    </mask>
                    <g mask="url(#mask0)">
                        <rect width="16" height="16"></rect>
                    </g>
                </svg>
            ),
            label: 'UHID Configuration',
        },
        amIDoctorOnly() && {
            key: 'activate-payments',
            svg: (
                <svg width="16" height="16" viewBox="0 0 16 13" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.875 0.625H14.125C15.082 0.625 15.875 1.41797 15.875 2.375V3.25H0.125V2.375C0.125 1.41797 0.890625 0.625 1.875 0.625ZM15.875 5.875V11.125C15.875 12.1094 15.082 12.875 14.125 12.875H1.875C0.890625 12.875 0.125 12.1094 0.125 11.125V5.875H15.875ZM3.1875 9.375C2.94141 9.375 2.75 9.59375 2.75 9.8125C2.75 10.0586 2.94141 10.25 3.1875 10.25H4.9375C5.15625 10.25 5.375 10.0586 5.375 9.8125C5.375 9.59375 5.15625 9.375 4.9375 9.375H3.1875ZM6.25 9.8125C6.25 10.0586 6.44141 10.25 6.6875 10.25H10.1875C10.4062 10.25 10.625 10.0586 10.625 9.8125C10.625 9.59375 10.4062 9.375 10.1875 9.375H6.6875C6.44141 9.375 6.25 9.59375 6.25 9.8125Z" />
                </svg>
            ),
            label: 'Activate Payments',
        },
        {
            key: 'messageConfiguration',
            svg: (
                <svg width="16" height="16" viewBox="0 0 14 15" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.75 0.75H12.25C13.207 0.75 14 1.54297 14 2.5V10.375C14 11.3594 13.207 12.125 12.25 12.125H8.44922L5.05859 14.668C4.92188 14.7773 4.75781 14.7773 4.59375 14.7227C4.45703 14.6406 4.375 14.5039 4.375 14.3125V12.125H1.75C0.765625 12.125 0 11.3594 0 10.375V2.5C0 1.54297 0.765625 0.75 1.75 0.75Z" />
                </svg>
            ),
            label: 'Message Configuration',
        },
        {
            key: 'manageTreatmentPackages',
            svg: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 512 512"
                >
                    <path d="M208 32c0-17.7 14.3-32 32-32h32c17.7 0 32 14.3 32 32V172.9l122-70.4c15.3-8.8 34.9-3.6 43.7 11.7l16 27.7c8.8 15.3 3.6 34.9-11.7 43.7L352 256l122 70.4c15.3 8.8 20.5 28.4 11.7 43.7l-16 27.7c-8.8 15.3-28.4 20.6-43.7 11.7L304 339.1V480c0 17.7-14.3 32-32 32H240c-17.7 0-32-14.3-32-32V339.1L86 409.6c-15.3 8.8-34.9 3.6-43.7-11.7l-16-27.7c-8.8-15.3-3.6-34.9 11.7-43.7L160 256 38 185.6c-15.3-8.8-20.5-28.4-11.7-43.7l16-27.7C51.1 98.8 70.7 93.6 86 102.4l122 70.4V32z" />
                </svg>
            ),
            label: 'Manage Treatment Packages',
        },
        amIDoctorOnly() && {
            key: 'staffRestriction',
            svg: (
                <svg width="16" height="16" viewBox="0 0 18 15" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.375 7.75C4.43359 7.75 2.875 6.19141 2.875 4.25C2.875 2.33594 4.43359 0.75 6.375 0.75C8.28906 0.75 9.875 2.33594 9.875 4.25C9.875 6.19141 8.28906 7.75 6.375 7.75ZM5.11719 9.0625H7.60547C8.80859 9.0625 9.90234 9.5 10.75 10.2109C10.75 10.2656 10.75 10.3203 10.75 10.375V13.875C10.75 14.2031 10.832 14.5039 10.9688 14.75H1.04297C0.605469 14.75 0.25 14.3945 0.25 13.957C0.25 11.25 2.41016 9.0625 5.11719 9.0625ZM14.6875 7.3125C14.1953 7.3125 13.8125 7.72266 13.8125 8.1875V9.5H15.5625V8.1875C15.5625 7.72266 15.1523 7.3125 14.6875 7.3125ZM12.5 8.1875C12.5 6.98438 13.457 6 14.6875 6C15.8906 6 16.875 6.98438 16.875 8.1875V9.5C17.3398 9.5 17.75 9.91016 17.75 10.375V13.875C17.75 14.3672 17.3398 14.75 16.875 14.75H12.5C12.0078 14.75 11.625 14.3672 11.625 13.875V10.375C11.625 9.91016 12.0078 9.5 12.5 9.5V8.1875Z" />
                </svg>
            ),
            label: 'Staff Restrictions',
        },
        {
            key: 'appointment-configs',
            svg: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                    fill="currentColor"
                    height="1em"
                >
                    <path d="M128 0c17.7 0 32 14.3 32 32V64H288V32c0-17.7 14.3-32 32-32s32 14.3 32 32V64h48c26.5 0 48 21.5 48 48v48H0V112C0 85.5 21.5 64 48 64H96V32c0-17.7 14.3-32 32-32zM0 192H448V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V192zm308.8 75c-14.7-14.7-38.6-14.7-53.3 0L240.6 282l53.3 53.3 14.9-14.9c14.7-14.7 14.7-38.6 0-53.3zM146.1 376.5c-4.1 4.1-7 9.2-8.4 14.9L128.5 428c-1.4 5.5 .2 11.2 4.2 15.2s9.7 5.6 15.2 4.2l36.6-9.2c5.6-1.4 10.8-4.3 14.9-8.4l71.9-71.9L218 304.6l-71.9 71.9z" />
                </svg>
            ),
            label: 'Queue Configuration',
        },
        {
            key: 'manage-services',
            svg: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="1em"
                    viewBox="0 0 576 512"
                    fill="currentColor"
                >
                    <path d="M142.5 15.6c4.7 12.4-1.6 26.2-14 30.9L85.2 62.7c-3.1 1.2-5.2 4.2-5.2 7.5V200c0 57.4 46.6 104 104 104h8 8c57.4 0 104-46.6 104-104V70.2c0-3.3-2.1-6.3-5.2-7.5L255.6 46.5c-12.4-4.7-18.7-18.5-14-30.9s18.5-18.7 30.9-14l43.2 16.2C337.5 25.9 352 46.8 352 70.2V200c0 78.5-59.5 143.1-135.8 151.1C219.9 414.1 272.1 464 336 464c66.3 0 120-53.7 120-120V268.3c-32.5-10.2-56-40.5-56-76.3c0-44.2 35.8-80 80-80s80 35.8 80 80c0 35.8-23.5 66.1-56 76.3V344c0 92.8-75.2 168-168 168c-90.4 0-164.1-71.4-167.8-160.8C91.7 343.3 32 278.6 32 200V70.2c0-23.3 14.5-44.2 36.3-52.4L111.6 1.5c12.4-4.7 26.2 1.6 30.9 14zM480 160a32 32 0 1 0 0 64 32 32 0 1 0 0-64z"></path>
                </svg>
            ),
            label: 'Manage Services',
        },
        {
            key: 'manage-treatment-packages',
            svg: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="1em"
                    viewBox="0 0 512 512"
                    fill="currentColor"
                >
                    <path d="M208 32c0-17.7 14.3-32 32-32h32c17.7 0 32 14.3 32 32V172.9l122-70.4c15.3-8.8 34.9-3.6 43.7 11.7l16 27.7c8.8 15.3 3.6 34.9-11.7 43.7L352 256l122 70.4c15.3 8.8 20.5 28.4 11.7 43.7l-16 27.7c-8.8 15.3-28.4 20.6-43.7 11.7L304 339.1V480c0 17.7-14.3 32-32 32H240c-17.7 0-32-14.3-32-32V339.1L86 409.6c-15.3 8.8-34.9 3.6-43.7-11.7l-16-27.7c-8.8-15.3-3.6-34.9 11.7-43.7L160 256 38 185.6c-15.3-8.8-20.5-28.4-11.7-43.7l16-27.7C51.1 98.8 70.7 93.6 86 102.4l122 70.4V32z"></path>
                </svg>
            ),
            label: 'Manage Treatment Packages',
        },
    ].filter(Boolean);

    const MENU_GROUP = showTreatmentPackages
        ? MENU_GROUP_3.filter((i) => i.key !== 'manageTreatmentPackages')
        : MENU_GROUP_3.filter((i) => i.key !== 'manage-treatment-packages');

    return (
        <div className="flex flex-col bg-white rounded-8">
            {MENU_GROUP.map((i, idx) => (
                <SettingComponent
                    i={i}
                    activeMenuItem={activeMenuItem}
                    setActiveMenuItem={setActiveMenuItem}
                    className={
                        idx === 0
                            ? 'rounded-t-8'
                            : idx === MENU_GROUP.length - 1
                            ? 'rounded-b-8'
                            : undefined
                    }
                    borderTop={
                        idx !== 0 &&
                        idx !== MENU_GROUP.findIndex((p) => p.key === activeMenuItem) + 1
                    }
                />
            ))}
        </div>
    );
};

const LeftPanel: React.FC<{
    setActiveMenuItem: (key: PROFILE_TABS_TYPE) => void;
    activeMenuItem: PROFILE_TABS_TYPE;
}> = (props) => {
    const { activeMenuItem, setActiveMenuItem } = props;

    return (
        <div className="flex w-300 flex-shrink-0 p-16 border-r-1 border-neutral-7 gap-16 flex-col h-full overflow-hidden bg-darwin-neutral_new-50">
            <div className="flex flex-col overflow-auto gap-16">
                <Group1Options
                    activeMenuItem={activeMenuItem}
                    setActiveMenuItem={setActiveMenuItem}
                />
                <Group2Options
                    activeMenuItem={activeMenuItem}
                    setActiveMenuItem={setActiveMenuItem}
                />
                <Group3Options
                    activeMenuItem={activeMenuItem}
                    setActiveMenuItem={setActiveMenuItem}
                />
            </div>
        </div>
    );
};

type configArrayType = [
    string,
    {
        title: string;
        summary: string;
        status: boolean;
        channels: string[];
        enabled_channels?: string[];
    },
];

interface DataType {
    key: React.Key;
    'msg-type': JSX.Element;
    'eka-app': JSX.Element;
    sms: JSX.Element;
    wa: JSX.Element;
    'p-wa': JSX.Element;
    'turn-on-off': JSX.Element;
}

const columns: TableColumnsType<DataType> = [
    {
        title: (
            <p className="uppercase font-700 self-center items-center justify-start text-12 text-darwin-neutral-700">
                Message type
            </p>
        ),
        dataIndex: 'msg-type',
        width: '30%',
        fixed: 'left',
    },
    {
        title: (
            <p className="uppercase font-700 justify-start text-12 text-darwin-neutral-700">
                <p className="flex flex-col text-12 text-darwin-neutral-700">
                    <span>Eka Patient App</span>
                    <span>(FREE)</span>
                </p>
            </p>
        ),
        dataIndex: 'eka-app',
    },
    {
        title: (
            <p className="uppercase font-700 flex items-center gap-8 justify-start">
                <p className="flex flex-col text-12 text-darwin-neutral-700">
                    <span>SMS</span>
                    <span>(0.14 CREDITS)</span>
                </p>
            </p>
        ),
        dataIndex: 'sms',
    },
    {
        title: (
            <p className="uppercase font-700 flex items-center gap-8 justify-start text-12 text-darwin-neutral-700">
                <p className="flex flex-col text-12 text-darwin-neutral-700">
                    <span>WHATSAPP</span>
                    <span>(0.66 CREDITS)</span>
                </p>
            </p>
        ),
        dataIndex: 'wa',
    },
    {
        title: (
            <p className="uppercase font-700 flex items-center gap-8 justify-start text-12 text-darwin-neutral-700">
                <p className="flex flex-col text-12 text-darwin-neutral-700">
                    <span>Own WHATSAPP</span>
                    <span>(FREE)</span>
                </p>
            </p>
        ),
        dataIndex: 'p-wa',
    },
    {
        title: (
            <p className="uppercase font-700 self-center items-center justify-start text-12 text-darwin-neutral-700">
                Turn ON/OFF
            </p>
        ),
        dataIndex: 'turn-on-off',
        fixed: 'right',
    },
];

const MessageConfiguration: React.FC<{ showEkaCommPop?: boolean }> = ({
    showEkaCommPop,
}): JSX.Element => {
    const [configArray, setConfigArray] = useState<configArrayType[]>([]);
    const [configFetchStatus, setConfigFetchStatus] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    const genieMessageConfig = useSelector(
        (state: GlobalState) => getGenieSetting(state, 'messageConfig') as MessageConfigType[],
    );
    const currBalance = useSelector((state: GlobalState) => getKhataCredits(state));
    const [rxMessageConfig, setRxMessageConfig] = useState(genieMessageConfig);

    const history = useHistory();

    const doctors = getDoctorSelectorList();

    const [selectedDocid, setSelectedDocid] = useState(() => doctors?.[0].id);

    useEffect(() => {
        if (showEkaCommPop) {
            dispatch(toggleShowEkaCommDialog(true));
        }
    }, [showEkaCommPop]);

    useEffect(() => {
        setConfigFetchStatus(true);
        fetchMessageConfig(selectedDocid)
            .then((t) => {
                if (t.success) {
                    setConfigArray(Object.entries(t.data));
                } else {
                    notification.error({
                        message: 'Something went wrong!',
                        placement: 'topRight',
                        duration: 5,
                    });
                }
                setConfigFetchStatus(false);
            })
            .catch(() => {
                notification.error({
                    message: 'Something went wrong!',
                    placement: 'topRight',
                    duration: 5,
                });
                console.error;
                setConfigFetchStatus(false);
            });
    }, [selectedDocid]);

    const tableData: DataType[] = [...configArray].map((c, idx) => {
        return {
            key: idx,
            'msg-type': (
                <p className="col-span-3 max-w-296 mr-8">
                    {' '}
                    <p className="font-700 text-14 text-neutral-1">{c[1].title}</p>
                    <span className="text-13 break-word text-darwin-neutrals-600">
                        {c[1].summary}
                    </span>
                </p>
            ),
            'eka-app': (
                <p className="col-span-2 flex items-center justify-start text-darwin-green-primary text-center">
                    {c[1].channels?.includes('pn') ? (
                        <AntTooltip content="Push notification can't be disabled">
                            <Checkbox
                                className="link-color"
                                checked={c[1].status && true}
                                // onChange={() => onToggleClick(data)}
                                disabled={true}
                                style={{
                                    transform: 'scale(1)',
                                }}
                            />
                        </AntTooltip>
                    ) : (
                        '-'
                    )}
                </p>
            ),
            sms: (
                <p
                    className="col-span-2 flex items-center justify-start text-center"
                    style={{ color: '#1A1A1A' }}
                >
                    {' '}
                    {c[1].channels?.includes('sms') ? (
                        <Checkbox
                            className="link-color"
                            checked={c[1].status && c[1].enabled_channels?.includes('sms')}
                            onChange={() => onChannelToggleClick(c, 'sms')}
                            disabled={!c[1].status}
                            style={{
                                transform: 'scale(1)',
                            }}
                        />
                    ) : (
                        '-'
                    )}
                </p>
            ),
            wa: (
                <p
                    className="col-span-2 flex items-center justify-start text-center"
                    style={{ color: '#1A1A1A' }}
                >
                    {c[1].channels?.includes('wa') ? (
                        <Checkbox
                            className="link-color"
                            checked={c[1].status && c[1].enabled_channels?.includes('wa')}
                            onChange={() => onChannelToggleClick(c, 'wa')}
                            disabled={!c[1].status}
                            style={{
                                transform: 'scale(1)',
                            }}
                        />
                    ) : (
                        '-'
                    )}
                </p>
            ),
            'p-wa': (
                <p className="col-span-2 flex items-center justify-start text-darwin-green-primary text-center">
                    {c[1].channels?.includes('p-wa') ? (
                        <AntTooltip content="Own whatsapp can't be disabled">
                            <Checkbox
                                className="link-color"
                                checked={c[1].status && true}
                                // onChange={() => onToggleClick(data)}
                                disabled={true}
                                style={{
                                    transform: 'scale(1)',
                                }}
                            />
                        </AntTooltip>
                    ) : (
                        '-'
                    )}
                </p>
            ),
            'turn-on-off': (
                <p className="col-span-1 flex items-center text-darwin-green-primary text-center">
                    <Switch
                        className="link-color"
                        checked={c[1].status}
                        onChange={() => onToggleClick(c)}
                        style={{
                            backgroundColor: c[1].status ? '#19A66A' : 'rgba(0,0,0,.25)',
                        }}
                    />
                </p>
            ),
        };
    });

    const rxConfigTableData: DataType[] = [...rxMessageConfig].map((c, idx) => {
        return {
            key: configArray.length + idx,
            'msg-type': (
                <p className="col-span-3 max-w-296 mr-8">
                    {' '}
                    <p className="font-700 text-14 text-neutral-1">{c.title}</p>
                    <span className="text-13 break-word text-darwin-neutrals-600">{c.summary}</span>
                </p>
            ),
            'eka-app': (
                <p className="col-span-2 flex items-center justify-start text-darwin-green-primary text-center"></p>
            ),
            sms: (
                <p
                    className="col-span-2 flex items-center justify-start text-center"
                    style={{ color: '#1A1A1A' }}
                ></p>
            ),
            wa: (
                <p
                    className="col-span-2 flex items-center justify-start text-center"
                    style={{ color: '#1A1A1A' }}
                ></p>
            ),
            'p-wa': (
                <p className="col-span-2 flex items-center justify-start text-darwin-green-primary text-center">
                    <AntTooltip content="Own whatsapp can't be disabled">
                        <Checkbox
                            className="link-color"
                            checked={c.status && true}
                            // onChange={() => onToggleClick(data)}
                            disabled={true}
                            style={{
                                transform: 'scale(1)',
                            }}
                        />
                    </AntTooltip>
                </p>
            ),
            'turn-on-off': (
                <p className="col-span-1 flex items-center text-darwin-green-primary text-center">
                    <Switch
                        className="link-color"
                        checked={c.status}
                        onChange={() => onToggleMessageConfigClick(c.id)}
                        style={{
                            backgroundColor: c.status ? '#19A66A' : 'rgba(0,0,0,.25)',
                        }}
                    />
                </p>
            ),
        };
    });

    const onToggleClick = (data: configArrayType) => {
        const updatedConfig: configArrayType[] = [...configArray].map((item) =>
            item[0] === data[0] ? [data[0], { ...data[1], status: !data[1].status }] : item,
        );
        setConfigArray(updatedConfig);
    };

    const onChannelToggleClick = (data: configArrayType, channel: 'wa' | 'sms') => {
        const updatedConfig: configArrayType[] = [...configArray].map((item) =>
            item[0] === data[0]
                ? [
                      data[0],
                      {
                          ...data[1],
                          enabled_channels: data[1].enabled_channels?.includes(channel)
                              ? data[1].enabled_channels.filter((c) => c !== channel)
                              : [...(data[1].enabled_channels ?? []), channel],
                      },
                  ]
                : item,
        );
        setConfigArray(updatedConfig);
    };

    const onToggleMessageConfigClick = (id: string) => {
        const messageConfig: MessageConfigType[] = rxMessageConfig?.map((conf) =>
            conf.id === id ? { ...conf, status: !conf.status } : conf,
        );

        setRxMessageConfig(messageConfig);
    };

    const onSave = () => {
        setIsLoading(true);
        const data = configArray.reduce(function (map, item) {
            map[item[0]] = item[1].status;
            map[`${item[0]}_enabled_channels`] = item[1].enabled_channels || item[1].channels;
            return map;
        }, {} as { [key: string]: boolean | string[] });

        pushMessageConfig(selectedDocid, data)
            .then((t) => {
                setIsLoading(false);
                if (t.success) {
                    notification.success({
                        message: `Config saved successfully!`,
                        placement: 'topRight',
                        duration: 5,
                    });
                } else {
                    notification.error({
                        message: 'Request failed retry!',
                        placement: 'topRight',
                        duration: 5,
                    });
                    track('ERROR', { on: 'pushMessageConfig', what: t.error });
                }
            })
            .catch((e) => {
                setIsLoading(false);
                notification.error({
                    message: 'Request failed retry!',
                    placement: 'topRight',
                    duration: 5,
                });
                track('ERROR', { on: 'pushMessageConfig', what: e.message });
            });

        dispatch(
            upsertGenieSetting(
                {
                    messageConfig: rxMessageConfig,
                },
                getMySingleDoctorId() || '',
            ),
        );
    };

    const onAddEkaCredits = () => {
        history.push(GO_TO_ADD_EKA_CREDITS);
        track('ADD_EKA_CREDITS');
    };

    return (
        <div className="w-full px-24 flex flex-col h-full space-between">
            <div className="flex w-full justify-between py-16">
                <div className="flex flex-col items-start gap-4">
                    <p className="text-16 font-700 text-neutral-black">Message Configuration</p>
                    <p className="text-14 font-400 text-neutral-black">
                        All messages utilise Eka Credits. View message types, costs, and set
                        delivery preferences here
                    </p>
                </div>
                <div
                    className={`flex rounded-8 gap-24 items-center self-center p-12 bg-darwin-neutral_new-50 ${getAclViewClassName(
                        SREV2.EKA_CREDITS,
                    )}`}
                    style={{ height: 'fit-content' }}
                >
                    {currBalance !== undefined && !isNaN(currBalance) && (
                        <div className="flex gap-4 items-center">
                            <span>
                                {currBalance && currBalance > 20
                                    ? ekaCreditsIcon('w-24 h-24')
                                    : ekaCreditsIconRed('w-18 h-18')}
                            </span>
                            <p
                                className={`font-700 text-14 ${
                                    currBalance && currBalance > 20
                                        ? 'text-darwin-neutral_new-1000'
                                        : 'text-red-primary'
                                }`}
                            >
                                Balance: {currBalance}
                            </p>
                        </div>
                    )}

                    <button
                        className="ml-auto flex text-13 items-center p-8 text-white bg-darwin-accent-bright-blue-800 rounded-8 font-700"
                        onClick={onAddEkaCredits}
                    >
                        Add Eka Credits
                    </button>
                </div>
            </div>
            {amIDoctorOnly() && (
                <div
                    className="cursor-pointer flex items-center justify-between mb-16 w-full px-16 py-8 rounded-8"
                    style={{
                        background: infoStripConstant(currBalance).bg,
                    }}
                    onClick={() => dispatch(toggleShowEkaCommDialog(true))}
                >
                    <div className="flex items-center gap-8">
                        <span className="m-4">{infoStripConstant(currBalance).icon}</span>
                        <p
                            className={`text-white text-14 ${
                                infoStripConstant(currBalance).fontWeight ?? 'font-400'
                            } ${infoStripConstant(currBalance).textColor}`}
                        >
                            {infoStripConstant(currBalance).text}
                        </p>
                    </div>
                    <span>
                        {rightArrowIcon(
                            currBalance && currBalance > 20 ? '' : 'fill-darwin-neutral_new-1000',
                        )}
                    </span>
                </div>
            )}

            {doctors.length > 1 ? (
                <div className="flex space-x-4 overflow-x-scroll w-full px-16">
                    {doctors?.map((doc) => (
                        <DoctorFilterStripUi
                            key={doc.id}
                            doctorName={`Dr. ${doc.personal.name.f || ''} ${
                                doc.personal.name.l || ''
                            }`.trim()}
                            doctorPic={doc.personal.pic}
                            onClick={() => {
                                setSelectedDocid(doc.id);
                            }}
                            showAptCount={false}
                            isSelected={selectedDocid === doc.id}
                        />
                    ))}
                </div>
            ) : null}

            <div className="pb-48 pt-0 items-center relative justify-center flex flex-col flex-1 overflow-auto hide-scrollbar h-full">
                {configFetchStatus ? (
                    <div className="flex flex-col items-center gap-12 justify-center h-full w-full">
                        <AppLoader />
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={[...tableData, ...rxConfigTableData]}
                        sticky={true}
                        className="msg-config-table rounded-8 border overflow-auto border-darwin-neutral_new-200"
                        pagination={false}
                        scroll={{ y: 650, x: 740 }}
                        key={'msg-config-key'}
                    />
                )}
            </div>

            <div
                className="relative rounded-8 bottom-24 w-full bg-white"
                style={{ boxShadow: '0px -2px 5px #D1D1D1' }}
            >
                <div className="flex items-center justify-end rounded-8 bg-actionPrimary bottom-24 inset-x-10 px-16 py-8 cta-row">
                    <button
                        onClick={onSave}
                        disabled={configFetchStatus || isLoading || configFetchStatus}
                        className="flex items-center gap-8 px-16 py-8 bg-white rounded-8 font-700 text-actionPrimary tooltip-wrapper"
                    >
                        {isLoading &&
                            loaderIcon('w-18 h-18 fill-actionPrimary font-700 animate-spin')}
                        <p>Save Changes</p>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MySettings;

export const createAllPtsExcelAndDownload = async (): Promise<void> => {
    const allPt = (await getAllPatients()) || [];
    const [small, large]: Patient[][] = allPt.reduce(
        (result, pt) => {
            result[
                pt.followUpDate && pt.followUpDate.toDateString() === new Date().toDateString()
                    ? 0
                    : 1
            ].push(pt);
            return result;
        },
        [[] as Patient[], [] as Patient[]],
    );

    const allPatients = [...small, ...large].filter((p) => !p.archived);

    return new Promise(async (resolve) => {
        track('EXPORT_ALL_PATIENTS');

        const df = (await getAddPDForms()).map((c) => {
            return {
                label: c.label,
                key: c.key,
            };
        });

        const defaultCols = DEFAULT_PT_COLUMNS.map((c) => ({ key: c, label: keyToLabel(c) }));

        const allCols = [...defaultCols, ...df];
        allCols.filter((c, i) => allCols.indexOf(c) == i);

        const requiredData = allPatients.map((p) => {
            return allCols.reduce((acc, curr) => {
                switch (curr.key) {
                    case PT_DETAILS:
                        return {
                            ...acc,
                            'Patient Name': [p.personal.name.f, p.personal.name.l]
                                .filter(Boolean)
                                .join(' '),
                            Gender: p.personal.gender ? p.personal.gender : '',
                            Age: getAgeString({
                                age: p.personal.age?.value,
                                month: p.personal.age?.month,
                                dob: p.personal.age?.dob,
                            }),
                        };
                    case CONTACT:
                        return {
                            ...acc,
                            Mobile: p.personal.phone?.n
                                ? `\t${(p.personal.phone?.c || '') + p.personal.phone?.n}`
                                : '',
                        };
                    case LAST_VISITED:
                        return {
                            ...acc,
                            'Last Visited': p.lastVisitedAt
                                ? `${moment(p.lastVisitedAt).format('DD-MM-YYYY')}`
                                : '-',
                        };
                    case FOLLOW_UP:
                        return {
                            ...acc,
                            'Follow Up': p.followUpDate
                                ? `${moment(p.followUpDate).format('DD-MM-YYYY')}`
                                : '-',
                        };
                    default:
                        return {
                            ...acc,
                            [`${curr.label} (DF)`]:
                                p.formData?.find((fd) => fd.key === curr.key)?.value || '-',
                        };
                }
            }, {});
        });

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const ws = XLSX.utils.json_to_sheet(requiredData);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const csvContent = XLSX.utils.sheet_to_csv(ws);

        const filename = `${[
            getDoc()?.profile.personal.name.f,
            getDoc()?.profile.personal.name.l,
        ].join(' ')}'s Patients ${moment().format('DD MMM YYYY h m A')}.csv`;

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        resolve();
    });
};
